import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { scaffoldMetaSchema, type ScaffoldMetaFormValues } from '../schema/scaffold';

interface Props {
  defaultValues?: Partial<ScaffoldMetaFormValues>;
  onNext: (values: ScaffoldMetaFormValues) => void;
  /** §R685-4: ②→미리보기 시도가 meta 미입력으로 되돌아온 경우 true — 안내 배너 노출 + 포커스. */
  metaRequired?: boolean;
}

const bannerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-2)',
  background: 'color-mix(in srgb, var(--color-warning) 12%, var(--color-surface-muted))',
  border: '1px solid var(--color-warning)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--spacing-3) var(--spacing-4)',
  marginBottom: 'var(--spacing-4)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
};

const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', marginBottom: 'var(--spacing-4)' };
const labelStyle: React.CSSProperties = { fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const inputStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-mono)',
  width: '100%',
};
const invalidInputStyle: React.CSSProperties = { borderColor: 'var(--color-danger)' };
const hintStyle: React.CSSProperties = { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' };
const errorStyle: React.CSSProperties = { fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', display: 'flex', alignItems: 'center', gap: 4 };

/** ①단계 — 프로젝트 메타 입력(projectName/groupId/domainRoot · zod 실시간 검증). */
export function ScaffoldMetaStep({ defaultValues, onNext, metaRequired }: Props) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ScaffoldMetaFormValues>({
    resolver: zodResolver(scaffoldMetaSchema),
    mode: 'onChange',
    // §R671-3(a) 정정: 기본 on 토글이 빈 sampleFeature 와 결합하면 ①단계가 즉시 검증 오류로 막혀
    // 기존 최소 플로우(메타 3필드→다음)가 회귀한다 — 유효한 기본값을 프리필해 무마찰 기본 유지.
    defaultValues: { projectName: '', groupId: '', domainRoot: '', includeSample: true, sampleFeature: 'sample-order', includeFrontend: false, includeCiCd: true, ...defaultValues },
  });

  const includeSample = watch('includeSample');
  const submit = handleSubmit((values) => onNext(values));

  const { ref: projectNameFieldRef, ...projectNameField } = register('projectName');
  const projectNameInputRef = useRef<HTMLInputElement | null>(null);

  // §R685-4 R685-T9: ②→미리보기 시도가 meta 미입력으로 ①단계로 되돌아온 경우 프로젝트명
  // 필드에 포커스를 옮겨 무엇을 먼저 채워야 하는지 즉시 드러낸다.
  useEffect(() => {
    if (metaRequired) {
      projectNameInputRef.current?.focus();
    }
  }, [metaRequired]);

  return (
    <div className="wizard-card" style={{ background: 'var(--color-surface-muted)', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-lg)', padding: 'var(--spacing-6)' }}>
      <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 'var(--spacing-1)' }}>
        {`① ${t('projectScaffold.step.meta')}`}
      </p>
      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-4)' }}>
        {t('projectScaffold.step.metaDesc')}
      </p>

      {metaRequired && (
        <div role="alert" style={bannerStyle}>
          <span aria-hidden="true">⚠</span>
          <span>{t('projectScaffold.meta.requiredNotice')}</span>
        </div>
      )}

      <form onSubmit={submit} noValidate>
        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="scaffold-project-name">{t('projectScaffold.field.projectName')}</label>
          <input
            id="scaffold-project-name"
            style={{ ...inputStyle, ...(errors.projectName ? invalidInputStyle : {}) }}
            type="text"
            aria-invalid={!!errors.projectName}
            aria-describedby="scaffold-project-name-hint"
            {...projectNameField}
            ref={(el) => {
              projectNameFieldRef(el);
              projectNameInputRef.current = el;
            }}
          />
          {errors.projectName ? (
            <span style={errorStyle} role="alert">⚠ {errors.projectName.message}</span>
          ) : (
            <span style={hintStyle} id="scaffold-project-name-hint">
              {t('projectScaffold.field.projectNameHint')} · <code>^[a-z][a-z0-9-]{'{1,49}'}$</code>
            </span>
          )}
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="scaffold-group-id">{t('projectScaffold.field.groupId')}</label>
          <input
            id="scaffold-group-id"
            style={{ ...inputStyle, ...(errors.groupId ? invalidInputStyle : {}) }}
            type="text"
            aria-invalid={!!errors.groupId}
            aria-describedby="scaffold-group-id-hint"
            {...register('groupId')}
          />
          {errors.groupId ? (
            <span style={errorStyle} role="alert">⚠ {errors.groupId.message}</span>
          ) : (
            <span style={hintStyle} id="scaffold-group-id-hint">
              {t('projectScaffold.field.groupIdHint')} · <code>^[a-z][a-z0-9.]{'{1,99}'}$</code>
            </span>
          )}
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle} htmlFor="scaffold-domain-root">{t('projectScaffold.field.domainRoot')}</label>
          <input
            id="scaffold-domain-root"
            style={{ ...inputStyle, ...(errors.domainRoot ? invalidInputStyle : {}) }}
            type="text"
            aria-invalid={!!errors.domainRoot}
            aria-describedby="scaffold-domain-root-hint"
            {...register('domainRoot')}
          />
          {errors.domainRoot ? (
            <span style={errorStyle} role="alert">⚠ {errors.domainRoot.message}</span>
          ) : (
            <span style={hintStyle} id="scaffold-domain-root-hint">
              {t('projectScaffold.field.domainRootHint')} · <code>^[a-z][a-z0-9.]{'{1,99}'}$</code>
            </span>
          )}
        </div>

        <div style={fieldStyle}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', cursor: 'pointer' }}>
            <input
              id="scaffold-include-sample"
              type="checkbox"
              style={{ accentColor: 'var(--color-brand-primary)', width: 15, height: 15 }}
              {...register('includeSample')}
            />
            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
              {t('projectScaffold.field.includeSample')}
            </span>
          </label>
          <span style={hintStyle}>{t('projectScaffold.field.includeSampleHint')}</span>
        </div>

        {includeSample && (
          <div style={fieldStyle}>
            <label style={labelStyle} htmlFor="scaffold-sample-feature">{t('projectScaffold.field.sampleFeature')}</label>
            <input
              id="scaffold-sample-feature"
              style={{ ...inputStyle, ...(errors.sampleFeature ? invalidInputStyle : {}) }}
              type="text"
              placeholder="sample-order"
              aria-invalid={!!errors.sampleFeature}
              aria-describedby="scaffold-sample-feature-hint"
              {...register('sampleFeature')}
            />
            {errors.sampleFeature ? (
              <span style={errorStyle} role="alert">⚠ {errors.sampleFeature.message}</span>
            ) : (
              <span style={hintStyle} id="scaffold-sample-feature-hint">
                {t('projectScaffold.field.sampleFeatureHint')} · <code>^[a-z][a-z0-9-]{'{1,49}'}$</code>
              </span>
            )}
          </div>
        )}

        {/* §R681-3(a) — FE 골격 포함(기본 off) */}
        <div style={fieldStyle}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', cursor: 'pointer' }}>
            <input
              id="scaffold-include-frontend"
              type="checkbox"
              style={{ accentColor: 'var(--color-brand-primary)', width: 15, height: 15 }}
              {...register('includeFrontend')}
            />
            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
              {t('projectScaffold.field.includeFrontend')}
            </span>
          </label>
          <span style={hintStyle}>{t('projectScaffold.field.includeFrontendHint')}</span>
        </div>

        {/* §R694-5 — CI/CD 설정 포함(기본 on) */}
        <div style={fieldStyle}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', cursor: 'pointer' }}>
            <input
              id="scaffold-include-ci-cd"
              type="checkbox"
              style={{ accentColor: 'var(--color-brand-primary)', width: 15, height: 15 }}
              {...register('includeCiCd')}
            />
            <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
              {t('projectScaffold.field.includeCiCd')}
            </span>
          </label>
          <span style={hintStyle}>{t('projectScaffold.field.includeCiCdHint')}</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--spacing-6)' }}>
          <button
            type="submit"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)',
              padding: 'var(--spacing-2) var(--spacing-4)', borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', border: 'none',
              background: 'var(--color-brand-primary)', color: 'var(--color-text-inverse)',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('projectScaffold.step.metaNext')}
          </button>
        </div>
      </form>
    </div>
  );
}
